import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('permissions') || '{}'); } catch(e) { return {}; }
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const saved = localStorage.getItem('user');
    if (token && saved) {
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
      setUser(JSON.parse(saved));
    }
    setLoading(false);
  }, []);

  const buildPermMap = (permArr) => {
    const map = {};
    (permArr || []).forEach(perm => {
      const [mod, action] = perm.split(':');
      if (!map[mod]) map[mod] = {};
      map[mod][action] = true;
    });
    return map;
  };

  const fetchPermissions = async (role, token) => {
    try {
      const pr = await axios.get(API + '/settings/roles/permissions', {
        headers: { Authorization: 'Bearer ' + token }
      });
      // Store ALL roles permissions
      const allPerms = {};
      (pr.data.permissions || []).forEach(p => {
        allPerms[p.role] = buildPermMap(p.permissions || []);
      });
      localStorage.setItem('allPermissions', JSON.stringify(allPerms));
      // Set current user's permissions
      const map = allPerms[role] || {};
      setPermissions(map);
      localStorage.setItem('permissions', JSON.stringify(map));
    } catch(e) {
      console.error('Failed to fetch permissions:', e);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true); setError(null);
      const r = await axios.post(API + '/users/login', { email, password });
      const { token, user: u, forcePasswordChange } = r.data;
      if (forcePasswordChange) localStorage.setItem('forcePasswordChange', 'true');
      else localStorage.removeItem('forcePasswordChange');
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(u));
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
      setUser(u);
      await fetchPermissions(u.role, token);
      return true;
    } catch(e) {
      setError(e.response?.data?.message || 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    localStorage.removeItem('forcePasswordChange');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setPermissions({});
  };

  const register = async (data) => {
    try {
      setLoading(true); setError(null);
      const r = await axios.post(API + '/users', data);
      return r.data;
    } catch(e) {
      setError(e.response?.data?.message || 'Failed');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      user, loading, error, login, logout, register,
      permissions, fetchPermissions,
      isAuthenticated: !!user, API
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
